import { Component } from '@theme/component';

/**
 * A custom element that counts down to a target moment.
 *
 * The target is provided as a unix timestamp in seconds via `data-target`,
 * so the moment is absolute regardless of the visitor's timezone. Values are
 * server-rendered for the initial paint and corrected on the first tick.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} days - The days value element.
 * @property {HTMLElement} hours - The hours value element.
 * @property {HTMLElement} minutes - The minutes value element.
 * @property {HTMLElement} seconds - The seconds value element.
 *
 * @extends Component<Refs>
 */
class CountdownTimer extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /** @type {ReturnType<typeof setInterval> | null} */
  #interval = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#target == null) return;

    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  /**
   * The target moment in milliseconds, or null when no valid target is set.
   *
   * @returns {number | null}
   */
  get #target() {
    const timestamp = Number(this.getAttribute('data-target'));

    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp * 1000 : null;
  }

  #stop() {
    if (this.#interval == null) return;

    clearInterval(this.#interval);
    this.#interval = null;
  }

  #tick() {
    const target = this.#target;
    if (target == null) return this.#stop();

    const remaining = Math.max(0, Math.floor((target - Date.now()) / 1000));

    this.#render({
      days: Math.floor(remaining / 86400),
      hours: Math.floor((remaining % 86400) / 3600),
      minutes: Math.floor((remaining % 3600) / 60),
      seconds: remaining % 60,
    });

    if (remaining > 0) return;

    this.#stop();

    if (this.hasAttribute('data-hide-when-complete')) {
      this.hidden = true;
    }
  }

  /**
   * @param {Record<'days' | 'hours' | 'minutes' | 'seconds', number>} values
   */
  #render(values) {
    for (const [unit, value] of Object.entries(values)) {
      const element = this.refs[unit];
      if (!(element instanceof HTMLElement)) continue;

      const text = String(value).padStart(2, '0');

      if (element.textContent !== text) {
        element.textContent = text;
      }
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
